import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { GoogleUser } from '../services/googleAuth';

// 订阅计划类型
export type SubscriptionPlan = 'none' | 'basic' | 'pro';

// 订阅信息接口
export interface Subscription {
  plan: SubscriptionPlan;
  status?: 'active' | 'cancelled' | 'expired';
  endDate?: string;
}

// 用户信息接口
export interface User {
  id: string;
  uid?: number;
  email: string;
  name?: string;
  displayName?: string;
  picture?: string;
  credits: number;
  isGoogleUser: boolean;
  subscription?: Subscription;
}

// 认证上下文接口
interface AuthContextType {
  user: User | null;
  isLoggedIn: boolean;
  isLoading: boolean;
  login: (userData: any) => Promise<void>;
  logout: () => void;
  updateCredits: (credits: number) => void;
  refreshUserData: () => Promise<void>;
}

// 创建认证上下文
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// 认证提供者组件
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load user data from localStorage
  useEffect(() => {
    const clearUserData = () => {
      console.log('🗑️ Clearing user data');
      setUser(null);
      localStorage.removeItem('glistar_user');
      localStorage.removeItem('glistar_token');
      localStorage.removeItem('glistar_google_auth');
    };

    const loadUserFromStorage = async () => {
      try {
        const storedUser = localStorage.getItem('glistar_user');
        
        console.log('🔍 Starting to load user data...');
        console.log('📦 Data in localStorage:', {
          hasUser: !!storedUser,
          hasToken: !!localStorage.getItem('glistar_token'),
          hasGoogleAuth: !!localStorage.getItem('glistar_google_auth')
        });
        
        if (storedUser) {
          const userData = JSON.parse(storedUser);
          const storedToken = localStorage.getItem('glistar_token');
          
          console.log('👤 Parsed user data:', {
            email: userData.email,
            isGoogleUser: userData.isGoogleUser,
            hasToken: !!storedToken
          });
          
          // Distinguish between Google users and email users
          if (userData.isGoogleUser) {
            // Google user: restore login state directly
            if (storedToken) {
              try {
                // Try to get latest credits info
                const creditsResponse = await fetchUserCredits(storedToken);
                console.log('creditsResponse',creditsResponse)
                const updatedUser = {  ...userData, credits: creditsResponse.data?.credits || creditsResponse.credits || userData.credits };
                setUser(updatedUser);
                localStorage.setItem('glistar_user', JSON.stringify(updatedUser));
                console.log('✅ Google user login state restored:', updatedUser.email);
              } catch (error) {
                // Keep login state even if fetching credits fails
                console.warn('Failed to fetch Google user credits, using cached data');
                setUser(userData);
                console.log('✅ Google user login state restored (cached):', userData.email);
              }
            } else {
              // Keep Google user logged in even without token
              setUser(userData);
              console.log('✅ Google user login state restored (no token):', userData.email);
            }
          } else {
            // Email user: need to validate token
            console.log('📧 Detected email user');
            if (storedToken) {
              try {
                await validateToken(storedToken);
                const creditsResponse = await fetchUserCredits(storedToken);
                const updatedUser = { ...userData, credits: creditsResponse.data?.credits || creditsResponse.credits || userData.credits };
                setUser(updatedUser);
                localStorage.setItem('glistar_user', JSON.stringify(updatedUser));
                console.log('✅ Email user login state restored:', userData.email);
              } catch (error) {
                console.warn('❌ Token validation failed, clearing login state', error);
                clearUserData();
              }
            } else {
              // Email user missing token, clear login state
              console.warn('❌ Email user missing token, clearing login state');
              clearUserData();
            }
          }
        }
      } catch (error) {
        console.error('Failed to load user data:', error);
        clearUserData();
      }
      setIsLoading(false);
    };

    loadUserFromStorage();
  }, []);

  // Validate token
  const validateToken = async (token: string) => {
    try {
      // Try to call API to validate token
      const response = await fetch(`${process.env.REACT_APP_API_URL}/user/validate`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      // Check HTTP status code
      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }
      
      const data = await response.json();
      if (data.status !== 200 || !data.data?.valid) {
        throw new Error('Token validation failed');
      }
      
      return true;
    } catch (error) {
      console.error('Token validation failed:', error);
      throw error; // Re-throw error for caller to handle
    }
  };

  // Login function
  const login = async (userData: any) => {
    setIsLoading(true);
    try {
      let user: User;

      // Handle Google login
      if (userData.picture || userData.sub) {
        // Get user credits info
        const result = await fetchGoogleLogin(userData);

        user = {
          id: userData.id || userData.sub,
          email: userData.email,
          name: userData.name,
          displayName: userData.name,
          picture: userData.picture,
          credits: result.data?.credits || result.credits || 0, // Default credits
          isGoogleUser: true
        };
        // Store token for Google user
        localStorage.setItem('glistar_google_auth', 'true');
        localStorage.setItem('glistar_token', result.data?.token || result.token || '');
      } 
      // Handle email login
      else if (userData.token) {
        // Get user credits info
        const creditsResponse = await fetchUserCredits(userData.token);
        user = {
          id: userData.user?.id?.toString() || '1',
          uid: userData.user?.uid,
          email: userData.user?.email || userData.email,
          name: userData.user?.displayName || userData.user?.email?.split('@')[0] || 'User',
          displayName: userData.user?.displayName,
          credits: creditsResponse.data?.credits || creditsResponse.credits || userData.user?.credits || 0,
          isGoogleUser: false
        };
        localStorage.setItem('glistar_token', userData.token);
      } else {
        throw new Error('Invalid user data');
      }

      setUser(user);
      localStorage.setItem('glistar_user', JSON.stringify(user));
      
      console.log('✅ User login successful:', user);
      console.log('✅ isLoggedIn should be:', !!user);
      console.log('✅ localStorage saved:', {
        user: localStorage.getItem('glistar_user') ? 'yes' : 'no',
        token: localStorage.getItem('glistar_token') ? 'yes' : 'no'
      });
    } catch (error) {
      console.error('Login processing failed:', error);
      throw error; // Re-throw error
    } finally {
      setIsLoading(false);
    }
  };

  // Logout function
  const logout = () => {
    setUser(null);
    localStorage.removeItem('glistar_user');
    localStorage.removeItem('glistar_token');
    localStorage.removeItem('glistar_google_auth');
    console.log('User logged out');
  };

  // Update credits
  const updateCredits = (credits: number) => {
    if (user) {
      const updatedUser = { ...user, credits };
      setUser(updatedUser);
      localStorage.setItem('glistar_user', JSON.stringify(updatedUser));
    }
  };

  // Refresh user data
  const refreshUserData = async () => {
    if (!user) return;

    try {
      const token = localStorage.getItem('glistar_token');
      if (token) {
        const creditsResponse = await fetchUserCredits(token);
        updateCredits(creditsResponse.data.credits);
      }
    } catch (error) {
      console.error('Failed to refresh user data:', error);
    }
  };

  const fetchGoogleLogin = async (userData: any) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/user/google-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(userData)
      });
      const data = await response.json()
      if (data.status === 200) {
        return data.data;
      } else {
        // If API not implemented yet, return default value
        return { credits: 0 };
      }
    } catch (error) {
      console.error('Failed to fetch credits:', error);
      return { credits: 0 };
    }
  }

  // Get user credits
  const fetchUserCredits = async (token: string) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/user/credits`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json()
      if (data.status === 200) {
        return data;
      } else {
        // If API not implemented yet, return default value
        return { credits: 0 };
      }
    } catch (error) {
      console.error('Failed to fetch credits:', error);
      return { credits: 0 };
    }
  };

  // 🐛 Debug mode detection
  const debugForceLogout = localStorage.getItem('debug_force_logout') === 'true';
  const debugClearUser = localStorage.getItem('debug_clear_user') === 'true';
  
  if (debugForceLogout) {
    console.log('🐛 Debug mode activated: Scenario 1 - Force isLoggedIn = false');
  }
  if (debugClearUser) {
    console.log('🐛 Debug mode activated: Scenario 2 - Force user = null');
  }

  const value: AuthContextType = {
    user: debugClearUser ? null : user,
    isLoggedIn: debugForceLogout ? false : !!user,
    isLoading,
    login,
    logout,
    updateCredits,
    refreshUserData
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook for using auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export default AuthContext;
